import * as React from "react";

export interface DebouncedCallback<T extends (...args: never[]) => unknown> {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  /** Cancel the pending invocation, if any. Safe to call when nothing is pending. */
  cancel: () => void;
  /**
   * Run the pending invocation now instead of waiting out the delay. Safe to
   * call when nothing is pending. For callers whose debounced edit must not
   * be lost to an unmount (e.g. a dialog closing mid-keystroke) — call this
   * before or during teardown, because unmount otherwise cancels.
   */
  flush: () => void;
}

/*
 * Only execute callback delay_ms time after the most recent attempt.
 * i.e. callback is executed "after the dust has settled".
 *
 * The pending timer is cancelled on unmount, so by default the wrapped
 * callback never fires against an unmounted component. A caller that would
 * lose data to that cancellation can call `.flush()` in its own unmount
 * cleanup, which runs before this hook's (cleanups run in reverse
 * registration order for hooks called earlier in the component).
 *
 * The returned function exposes `.cancel()` — drop the pending invocation
 * (useful when a synchronous event supersedes the debounced one) — and
 * `.flush()` — run it immediately.
 */
export function useDebouncedCallback<
  T extends (...args: Parameters<T>) => ReturnType<T>,
>(fn: T, delay_ms: number = 0): DebouncedCallback<T> {
  const timeout = React.useRef<number>();
  const pending = React.useRef<(() => void) | null>(null);

  const cancel = React.useCallback(() => {
    window.clearTimeout(timeout.current);
    timeout.current = undefined;
    pending.current = null;
  }, []);

  const flush = React.useCallback(() => {
    const run = pending.current;
    cancel();
    run?.();
  }, [cancel]);

  React.useEffect(() => {
    return cancel;
  }, [cancel]);

  return React.useMemo(() => {
    const debounced = (...args: Parameters<T>): ReturnType<T> | undefined => {
      let result: ReturnType<T> | undefined = undefined;

      const later = () => {
        timeout.current = undefined;
        pending.current = null;
        result = fn(...args);
      };

      window.clearTimeout(timeout.current);
      pending.current = later;
      timeout.current = window.setTimeout(later, delay_ms);

      return result;
    };
    debounced.cancel = cancel;
    debounced.flush = flush;
    return debounced;
  }, [fn, delay_ms, cancel, flush]);
}
